import { cn } from "@/lib/utils";

type Variant = "mark" | "lockup";
type Tone = "auto" | "brand" | "mono-light" | "mono-dark";

interface SyraLogoProps {
  size?: number;
  variant?: Variant;
  tone?: Tone;
  className?: string;
  title?: string;
}

/**
 * Canonical SYRA "Aperture Orb" logo. Single source of truth.
 * Visual: green sphere with specular highlight, dark inner ring and central nucleus.
 * - mark: only the orb
 * - lockup: orb + SYRA wordmark
 */
export function SyraLogo({
  size = 32,
  variant = "mark",
  tone = "brand",
  className,
  title = "SYRA",
}: SyraLogoProps) {
  const isMonoLight = tone === "mono-light";
  const isMonoDark = tone === "mono-dark";
  const mono = isMonoLight || isMonoDark;
  const monoColor = isMonoLight ? "hsl(0 0% 100%)" : "hsl(222 47% 11%)";
  const nucleusColor = mono ? monoColor : "hsl(222 47% 6%)";
  const ringColor = mono ? monoColor : "hsl(157 79% 18%)";

  const Mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className="shrink-0"
    >
      <defs>
        <radialGradient id="syraOrbSphere" cx="0.38" cy="0.34" r="0.78">
          <stop offset="0%" stopColor="hsl(150 85% 72%)" />
          <stop offset="45%" stopColor="hsl(152 73% 50%)" />
          <stop offset="100%" stopColor="hsl(157 79% 28%)" />
        </radialGradient>
        <radialGradient id="syraOrbHighlight" cx="0.32" cy="0.28" r="0.32">
          <stop offset="0%" stopColor="hsla(0,0%,100%,0.65)" />
          <stop offset="100%" stopColor="hsla(0,0%,100%,0)" />
        </radialGradient>
      </defs>

      <circle
        cx="32"
        cy="32"
        r="26"
        fill={mono ? monoColor : "url(#syraOrbSphere)"}
      />

      {!mono && (
        <ellipse cx="24" cy="22" rx="14" ry="10" fill="url(#syraOrbHighlight)" />
      )}

      <circle
        cx="32"
        cy="32"
        r="9"
        fill="none"
        stroke={ringColor}
        strokeWidth="2.5"
      />

      <circle cx="32" cy="32" r="5.5" fill={nucleusColor} />
    </svg>
  );

  if (variant === "mark") {
    return <span className={cn("inline-flex", className)}>{Mark}</span>;
  }

  const wordTone = isMonoLight ? "text-white" : "text-foreground";

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      {Mark}
      <span
        className={cn(
          "font-bold tracking-[0.18em] leading-none",
          wordTone,
        )}
        style={{ fontSize: Math.round(size * 0.62) }}
      >
        SYRA
      </span>
    </span>
  );
}

export default SyraLogo;
